import { masters } from "@nexseed/shared";
import { FlaskConical } from "lucide-react";
import { defineMaster, type MasterRow } from "../spec";

/**
 * QA Team, was QC Analysts (user, 2026-10-23; renamed 2026-10-24): the lab's named staff per plant,
 * each one's type, and which tests each does. A test is allocated to a login user, often one
 * in-charge for Moisture and PP with several people under them; when the result is recorded on the
 * handheld, "Tested by" is picked from this list. The type (QA Manager … Junior QA Analyst) is for
 * the dropdowns that list only some of them. Key and path keep the old name: handhelds call it.
 */
export const qcAnalysts = defineMaster({
  key: "qc-analysts",
  title: "QA Team",
  singular: "Team member",
  description:
    "The lab's QA team per plant: each one's type and the tests each does. Picked as Tested by when a Moisture, PP or Germination result is recorded.",
  icon: FlaskConical,
  schema: masters.qcAnalysts.create,
  searchPlaceholder: "Search name or phone",
  defaultSort: "name",
  optionLabel: (r: MasterRow) => String(r.name),
  columns: [
    { id: "name", header: "Name", sortable: true },
    {
      id: "memberType",
      header: "Type",
      sortable: true,
      cell: (r) =>
        masters.QA_TEAM_TYPE_LABELS[r.memberType as masters.QaTeamType] ?? "—",
    },
    {
      id: "facilityId",
      header: "Plant",
      kind: "reference",
      reference: "facilities",
      sortable: true,
    },
    { id: "phone", header: "Phone", kind: "mono" },
    { id: "doesMoisture", header: "Moisture", kind: "bool" },
    { id: "doesPp", header: "PP", kind: "bool" },
    { id: "doesGermination", header: "Germination", kind: "bool" },
    { id: "isActive", header: "Status", kind: "status" },
  ],
  fields: [
    {
      name: "facilityId",
      label: "Plant",
      type: "reference",
      master: "facilities",
      required: true,
      colSpan: 2,
    },
    { name: "name", label: "Name", type: "text", required: true, colSpan: 2 },
    {
      name: "memberType",
      label: "Type",
      type: "enum",
      options: masters.QA_TEAM_TYPES.map((value) => ({
        value,
        label: masters.QA_TEAM_TYPE_LABELS[value],
      })),
      required: true,
    },
    { name: "phone", label: "Phone", type: "phone" },
    { name: "doesMoisture", label: "Does Moisture", type: "switch" },
    { name: "doesPp", label: "Does Physical Purity", type: "switch" },
    { name: "doesGermination", label: "Does Germination", type: "switch" },
    { name: "isActive", label: "Active", type: "switch" },
  ],
});
